/**
 * Single Socket.IO client per wallet for all PvP flows (lobby, matchmaking, battle).
 * Avoids disconnecting when navigating waiting → battle (which falsely triggered
 * opponent-disconnected on the server for the old socket id).
 */
import { io, Socket } from 'socket.io-client'
import config from '../utils/config'
import { getAuthToken } from '../utils/authToken'

let sharedSocket: Socket | null = null
let sharedWallet: string | null = null

export function getSharedPvPSocket (walletAddress: string): Socket {
  if (!walletAddress) {
    throw new Error('getSharedPvPSocket: walletAddress required')
  }
  if (sharedSocket && sharedWallet === walletAddress) {
    return sharedSocket
  }
  if (sharedSocket) {
    sharedSocket.removeAllListeners()
    sharedSocket.disconnect()
    sharedSocket = null
    sharedWallet = null
  }
  const apiKey = process.env.REACT_APP_X_API_KEY || ''
  const token = getAuthToken()
  sharedWallet = walletAddress
  sharedSocket = io(config.socketURL as string, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    auth: {
      ...(token ? { token } : {}),
      ...(apiKey ? { 'x-api-key': apiKey } : {}),
    },
    extraHeaders: apiKey ? { 'x-api-key': apiKey } : {},
  })

  const markPresence = () => {
    const w = sharedWallet
    if (!w || !sharedSocket) return
    sharedSocket.emit('join-wallet-room', { walletAddress: w })
    sharedSocket.emit('user:online', { walletAddress: w })
  }
  sharedSocket.on('connect', markPresence)
  sharedSocket.on('reconnect', markPresence)
  if (sharedSocket.connected) markPresence()

  return sharedSocket
}

/** Call when wallet disconnects / address cleared so the shared socket is torn down. */
export function teardownSharedPvPSocket (): void {
  if (sharedSocket) {
    sharedSocket.removeAllListeners()
    sharedSocket.disconnect()
    sharedSocket = null
    sharedWallet = null
  }
}

export function getSharedPvPSocketWallet (): string | null {
  return sharedWallet
}

/** On full document unload, close the shared socket so presence updates to offline on the server. */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', (event: PageTransitionEvent) => {
    if (event.persisted) return
    if (sharedSocket?.connected) {
      sharedSocket.disconnect()
    }
  })
}
